"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Receipt, AlertCircle, ExternalLink, ArrowLeft, CheckCircle } from "lucide-react"
import axios from "axios"
import "../styles/checkout-boleto.css"

function CheckoutBoleto({ pedido, voltar }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: pedido.usuario?.email || "",
    identificationType: "CPF",
    identificationNumber: "",
    zip_code: "",
    street_name: "",
    street_number: "",
    neighborhood: "",
    city: "",
    federal_unit: "",
  })

  const [boletoUrl, setBoletoUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState("")
  const [sdkLoading, setSdkLoading] = useState(true)

  useEffect(() => {
    const script = document.createElement("script")
    script.src = "https://sdk.mercadopago.com/js/v2"
    script.async = true
    script.onload = () => {
      console.log("MercadoPago SDK carregado!")
      setSdkLoading(false)
    }
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])
  useEffect(() => {
    // Limpa possíveis dados anteriores do Mercado Pago (ex: Pix ou Cartão)
    const cleanup = () => {
      if (window.MercadoPago) {
        try {
          window.MercadoPago.clearSession && window.MercadoPago.clearSession()
        } catch (e) {
          console.warn("Não foi possível limpar sessão do MercadoPago:", e)
        }
      }
    }
  
    cleanup()
  
    return cleanup
  }, [])

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setErro("")

    try {
      const token = localStorage.getItem("token")
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/pagamento/boleto`,
        {
          pedidoId: pedido._id,
          totalPedido: pedido.total,
          email: form.email,
          identificationType: form.identificationType,
          identificationNumber: form.identificationNumber,
          endereco: {
            ...form,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      setBoletoUrl(response.data.pagamentoBoleto.boleto_url)
    } catch (err) {
      console.error("Erro ao gerar boleto", err)
      setErro("Erro ao gerar boleto. Verifique os dados e tente novamente.")
    } finally {
      setLoading(false)
    }
  }

  if (sdkLoading) {
    return (
      <div className="ty-boleto-loading">
        <div className="ty-loading-spinner"></div>
        <p>Carregando formulário de boleto...</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="ty-checkout-container"
    >
      <h2 className="ty-checkout-title">Pagamento via Boleto</h2>

      {erro && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="ty-checkout-erro">
          <AlertCircle size={18} />
          {erro}
        </motion.div>
      )}

      {boletoUrl ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="ty-boleto-gerado">
          <div className="ty-boleto-sucesso">
            <CheckCircle size={50} className="ty-success-icon" />
            <h3>Sua compra foi registrada no nosso sistema!</h3>

            
          </div>
          <div className="ty-success-message">
              <p>Vamos aguardar a confirmação do pagamento pelo banco.</p>
              <p>Compras com boleto podem demorar até 3 dias.</p>
              <p>Mas fique tranquilo, a confirmação é automática.</p>
            </div>

            <div className="ty-boleto-info">
              <Receipt size={24} className="ty-boleto-icon" />
              <p>Clique no botão abaixo para visualizar e imprimir seu boleto.</p>
            </div>
          <motion.a
            href={boletoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ty-btn-visualizar-boleto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ExternalLink size={18} />
            Visualizar Boleto
          </motion.a>

          <motion.button
            onClick={voltar}
            className="ty-btn-voltar"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <ArrowLeft size={18} />
            Voltar
          </motion.button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="ty-boleto-form">
          <div className="ty-form-grid">
            <div className="ty-form-group">
              <label htmlFor="firstName">Nome</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Seu nome"
                required
              />
            </div>

            <div className="ty-form-group">
              <label htmlFor="lastName">Sobrenome</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Seu sobrenome"
                required
              />
            </div>
          </div>

          <div className="ty-form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Seu e-mail"
              required
            />
          </div>

          <div className="ty-form-group">
            <label htmlFor="identificationNumber">CPF</label>
            <input
              type="text"
              id="identificationNumber"
              name="identificationNumber"
              value={form.identificationNumber}
              onChange={handleChange}
              placeholder="Seu CPF"
              required
            />
          </div>

          <div className="ty-form-group">
            <label htmlFor="zip_code">CEP</label>
            <input
              type="text"
              id="zip_code"
              name="zip_code"
              value={form.zip_code}
              onChange={handleChange}
              placeholder="Seu CEP"
              required
            />
          </div>

          <div className="ty-form-grid">
            <div className="ty-form-group">
              <label htmlFor="street_name">Rua</label>
              <input
                type="text"
                id="street_name"
                name="street_name"
                value={form.street_name}
                onChange={handleChange}
                placeholder="Nome da rua"
                required
              />
            </div>

            <div className="ty-form-group">
              <label htmlFor="street_number">Número</label>
              <input
                type="text"
                id="street_number"
                name="street_number"
                value={form.street_number}
                onChange={handleChange}
                placeholder="Número"
                required
              />
            </div>
          </div>

          <div className="ty-form-group">
            <label htmlFor="neighborhood">Bairro</label>
            <input
              type="text"
              id="neighborhood"
              name="neighborhood"
              value={form.neighborhood}
              onChange={handleChange}
              placeholder="Seu bairro"
              required
            />
          </div>

          <div className="ty-form-grid">
            <div className="ty-form-group">
              <label htmlFor="city">Cidade</label>
              <input
                type="text"
                id="city"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Sua cidade"
                required
              />
            </div>

            <div className="ty-form-group">
              <label htmlFor="federal_unit">Estado</label>
              <input
                type="text"
                id="federal_unit"
                name="federal_unit"
                value={form.federal_unit}
                onChange={handleChange}
                placeholder="UF"
                required
              />
            </div>
          </div>

          <div className="ty-form-actions">
            <motion.button
              type="button"
              onClick={voltar}
              className="ty-btn-cancelar"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <ArrowLeft size={18} />
              Voltar
            </motion.button>

            <motion.button
              type="submit"
              className="ty-btn-gerar-boleto"
              disabled={loading}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {loading ? (
                <>
                  <div className="ty-button-loader"></div>
                  Gerando Boleto...
                </>
              ) : (
                <>
                  <Receipt size={18} />
                  Gerar Boleto
                </>
              )}
            </motion.button>
          </div>
        </form>
      )}
    </motion.div>
  )
}

export default CheckoutBoleto
